import React, { useState, useEffect } from 'react';
import { X, Plus, Tag } from 'lucide-react';
import useTagStore from '../../store/tagStore';
import useAuthStore from '../../store/authStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const TagSelector = ({ selectedTags = [], onTagsChange, className = '' }) => {
  const { userProfile } = useAuthStore();
  const { tags, subscribeToTags, createTag, loading } = useTagStore();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    if (userProfile?.familyId) {
      subscribeToTags(userProfile.familyId);
    }
  }, [userProfile?.familyId, subscribeToTags]);

  const handleTagToggle = (tagId) => {
    const isSelected = selectedTags.includes(tagId);
    if (isSelected) {
      onTagsChange(selectedTags.filter(id => id !== tagId));
    } else {
      onTagsChange([...selectedTags, tagId]);
    }
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();
    if (!newTagName.trim()) {
      setCreateError('Tag name is required');
      return;
    }

    const result = await createTag(userProfile.familyId, userProfile.uid, {
      name: newTagName.trim(),
      color: '#6B7280' // Default gray color
    });

    if (result.success) {
      setNewTagName('');
      setShowCreateForm(false);
      setCreateError('');
      // Auto-select the newly created tag
      onTagsChange([...selectedTags, result.id]);
    } else {
      setCreateError(result.error);
    }
  };

  const getSelectedTagsDisplay = () => {
    return tags.filter(tag => selectedTags.includes(tag.id));
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Tags (Optional)
      </label>
      
      {/* Selected Tags Display */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {getSelectedTagsDisplay().map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
              style={{ backgroundColor: tag.color }}
            >
              {tag.name}
              <button
                type="button"
                onClick={() => handleTagToggle(tag.id)}
                className="ml-1 hover:bg-black hover:bg-opacity-20 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Available Tags */}
      {tags.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-gray-500 mb-2">Available tags:</p>
          <div className="flex flex-wrap gap-2">
            {tags
              .filter(tag => !selectedTags.includes(tag.id))
              .map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => handleTagToggle(tag.id)}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border border-gray-300 hover:border-gray-400 transition-colors"
                  style={{ 
                    color: tag.color,
                    borderColor: tag.color + '40',
                    backgroundColor: tag.color + '10'
                  }}
                >
                  <div
                    className="w-2 h-2 rounded-full mr-1"
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Create New Tag */}
      {!showCreateForm ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowCreateForm(true)}
          className="text-xs"
        >
          <Plus className="h-3 w-3 mr-1" />
          Create New Tag
        </Button>
      ) : (
        <form onSubmit={handleCreateTag} className="space-y-2">
          {createError && (
            <p className="text-xs text-red-600">{createError}</p>
          )}
          <div className="flex space-x-2">
            <Input
              type="text"
              placeholder="Tag name"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              className="text-xs h-8"
            />
            <Button
              type="submit"
              size="sm"
              disabled={loading}
              className="h-8 text-xs"
            >
              {loading ? 'Creating...' : 'Create'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setShowCreateForm(false);
                setNewTagName('');
                setCreateError('');
              }}
              className="h-8 text-xs"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {tags.length === 0 && !showCreateForm && (
        <div className="text-center py-4 border-2 border-dashed border-gray-300 rounded-lg">
          <Tag className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500 mb-2">No tags created yet</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowCreateForm(true)}
            className="text-xs"
          >
            <Plus className="h-3 w-3 mr-1" />
            Create Your First Tag
          </Button>
        </div>
      )}
    </div>
  );
};

export default TagSelector;
